/**
 * kategorie-section-forms — the web-category form body, shared by the full-page create surface
 * (`KategorieForm`) and the edit modal (`KategorieEditModal`), like `pobocka-section-forms`.
 * Renders `<form id={KATEGORIE_FORM_ID}>` with fields only; the chrome supplies the submit button
 * through the HTML `form` attribute, so Enter and native validation work.
 */

import type { FormEvent } from 'react';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import type { KategorieWriteBody, PujcovnaKategorie } from './api';
import { SlugField } from './SlugField';

export const KATEGORIE_FORM_ID = 'pujcovna-kategorie-form';

export interface KategorieValue {
  nazev: string;
  ikona: string;
  /** Public URL key; '' = none. */
  slug: string;
}

export function kategorieSeed(k?: PujcovnaKategorie | null): KategorieValue {
  return { nazev: k?.nazev ?? '', ikona: k?.ikona ?? '', slug: k?.slug ?? '' };
}

/** Trimmed; the order (`poradi`) is not edited here — a new category goes to the end of the list. */
export function kategorieToBody(v: KategorieValue): KategorieWriteBody & { nazev: string } {
  return { nazev: v.nazev.trim(), ikona: v.ikona.trim(), slug: v.slug.trim() };
}

/**
 * The server accepts lower-case letters, digits and hyphens; the same rule gives early feedback.
 * Surrounding whitespace is allowed on purpose: the native check runs on the raw input, a pasted
 * " camper " must reach the submit handler, and `kategorieToBody` trims it (as the server does).
 */
export const IKONA_PATTERN = '\\s*[a-z0-9\\-]*\\s*';

export function KategorieFormBody({ initial, busy, onSubmit, onChange }: BodyProps<KategorieValue>) {
  const { form, set } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={KATEGORIE_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Název kategorie" required>
          <TextInput
            value={form.nazev}
            maxLength={100}
            placeholder="Osobní malé"
            onChange={(e) => set('nazev', e.currentTarget.value)}
          />
        </Field>
        <Field label="Ikona" optional="volitelné" hint="Klíč ikony pro veřejný web, např. suv. Malá písmena, číslice, pomlčky.">
          <TextInput
            value={form.ikona}
            maxLength={40}
            pattern={IKONA_PATTERN}
            title="Malá písmena, číslice a pomlčky"
            onChange={(e) => set('ikona', e.currentTarget.value)}
          />
        </Field>
        <SlugField
          value={form.slug}
          onChange={(v) => set('slug', v)}
          nazev={form.nazev}
          placeholder="suv"
          hint="Část adresy veřejného webu, např. suv. Malá písmena bez diakritiky, číslice, pomlčky; jedinečný mezi kategoriemi."
        />
      </Fields>
    </form>
  );
}
